// Choosing a planet's alignment (docs/02-Game-Design/Alignments.md). When a
// constellation first surrounds a planet, the player must pick one of the
// alignments the level offers for it; the pick is for the night and stays
// through removals. It is a move of the play, so the Worker replays it.
import { isAlignmentId, type AlignmentId } from '../alignments/catalogue';
import { planetsOf } from '../level/schema';
import type { PlayState } from './types';

export type AlignCode = 'aligned' | 'align_refused';

export type AlignRefusal = 'no_planet' | 'not_surrounded' | 'already_chosen' | 'not_offered';

export interface AlignOutcome {
  code: AlignCode;
  state: PlayState;
  reason?: AlignRefusal;
}

/** The alignment chosen for a planet, or null. */
export function alignmentChoiceOf(state: PlayState, planet: number): AlignmentId | null {
  return state.alignments.find((a) => a.planet === planet)?.alignment ?? null;
}

/**
 * The planet waiting for a choice: the first surrounded planet (in the order
 * the constellations were spelled) that offers alignments and has none
 * chosen, or null. While one waits, the play cannot go on.
 */
export function pendingAlignment(state: PlayState): number | null {
  const chosen = new Set(state.alignments.map((a) => a.planet));
  const planets = new Map(planetsOf(state.level).map((p) => [p.id, p]));
  for (const c of state.constellations) {
    if (!c.planet) continue;
    const planet = planets.get(c.planet.id);
    if (!planet || !planet.alignments?.length || chosen.has(planet.id)) continue;
    return planet.id;
  }
  return null;
}

/** Record the choice: the planet must exist, be surrounded, offer the alignment, and have none chosen yet. */
export function chooseAlignment(state: PlayState, planet: number, alignment: string): AlignOutcome {
  const def = planetsOf(state.level).find((p) => p.id === planet);
  if (!def) return { code: 'align_refused', state, reason: 'no_planet' };
  if (!state.constellations.some((c) => c.planet?.id === planet)) return { code: 'align_refused', state, reason: 'not_surrounded' };
  if (state.alignments.some((a) => a.planet === planet)) return { code: 'align_refused', state, reason: 'already_chosen' };
  if (!isAlignmentId(alignment) || !def.alignments?.includes(alignment)) return { code: 'align_refused', state, reason: 'not_offered' };
  return { code: 'aligned', state: { ...state, alignments: [...state.alignments, { planet, alignment }] } };
}
